<template>
    <PortableText :value="blocks" :components="mergedComponents" />
</template>

<script setup lang="ts">
    import { PortableText } from '@portabletext/vue';
    import type { PortableTextComponents } from '@portabletext/vue';

    interface Serializers {
        types?: Record<string, any>;
        marks?: Record<string, any>;
    }

    const props = defineProps<{
        blocks: any[];
        serializers?: Serializers;
    }>();

    // Helper: @portabletext/vue passes children via Vue slots, not props
    const wrap =
        (tag: string) =>
        (_props: any, { slots }: any) =>
            h(tag, slots.default?.());

    // Default components for rendering different block types
    const defaultComponents: PortableTextComponents = {
        marks: {
            link: ({ value }: any, { slots }: any) => {
                const href = value?.href || '';
                const target = href.startsWith('http') ? '_blank' : undefined;
                const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
                return h('a', { href, target, rel }, slots.default?.());
            },
            strong: wrap('strong'),
            em: wrap('em'),
            underline: (_props: any, { slots }: any) => h('span', { class: 'underline' }, slots.default?.()),
            'strike-through': wrap('s'),
        },
        block: {
            h1: wrap('h1'),
            h2: wrap('h2'),
            h3: wrap('h3'),
            h4: wrap('h4'),
            h5: wrap('h5'),
            h6: wrap('h6'),
            blockquote: wrap('blockquote'),
            normal: wrap('p'),
        },
        list: {
            bullet: wrap('ul'),
            number: wrap('ol'),
        },
        listItem: {
            bullet: wrap('li'),
            number: wrap('li'),
        },
    };

    // Merge default components with serializers from props
    const mergedComponents = computed<PortableTextComponents>(() => {
        if (!props.serializers) {
            return defaultComponents;
        }

        const components: PortableTextComponents = { ...defaultComponents };

        // Map serializers.types to components.types
        if (props.serializers.types) {
            components.types = {};
            for (const [key, Component] of Object.entries(props.serializers.types)) {
                // Wrap the component to pass the value as props
                components.types[key] = ({ value }: any) => {
                    return h(Component, { ...value });
                };
            }
        }

        // Map serializers.marks to components.marks
        if (props.serializers.marks) {
            components.marks = { ...defaultComponents.marks };
            for (const [key, Component] of Object.entries(props.serializers.marks)) {
                components.marks[key] = ({ value }: any, { slots }: any) => {
                    return h(Component, { ...value }, () => slots.default?.());
                };
            }
        }

        return components;
    });
</script>
